import { useTranslation } from 'react-i18next';
import { RefreshCw, SlidersHorizontal } from 'lucide-react';
import { useSocialLinkingPlatforms } from '@/hooks/use-social-linking-platforms';
import { SOCIAL_LINKING_PLATFORMS } from '@/lib/account-settings/visibleSocialLinkingPlatforms';
import { ANALYTICS_RANGES, type AnalyticsRange } from '@/lib/social-analytics/analyticsFilters';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelectPopover } from './MultiSelectPopover';

type AnalyticsFilterBarProps = {
  mode?: 'overview' | 'platform-only';
  range: AnalyticsRange;
  platforms: string[];
  /** Platforms of the viewer's analytics connections; keeps Facebook listed once one exists. */
  connectedPlatforms?: string[];
  refreshing?: boolean;
  refreshDisabled?: boolean;
  refreshLabel?: string;
  onRangeChange: (value: AnalyticsRange) => void;
  onPlatformsChange: (value: string[]) => void;
  onRefresh?: () => void;
};

/** Page-wide filters: the cards, chart, breakdown, insights and table all respond to these. */
export const AnalyticsFilterBar = ({
  mode = 'overview',
  range,
  platforms,
  connectedPlatforms = [],
  refreshing = false,
  refreshDisabled = false,
  refreshLabel,
  onRangeChange,
  onPlatformsChange,
  onRefresh,
}: AnalyticsFilterBarProps) => {
  const { t } = useTranslation();
  const linkable = useSocialLinkingPlatforms();
  const showFacebook = linkable.includes('facebook') || connectedPlatforms.includes('facebook');
  const platformChoices = SOCIAL_LINKING_PLATFORMS.filter(
    (platform) => platform !== 'facebook' || showFacebook,
  );

  if (mode === 'platform-only') {
    // The CPM tab keeps its single-platform select until sub-project 3 redesigns that tab.
    const platformValue = platforms.length === 1 ? platforms[0] : 'all';
    return (
      <section aria-label={t('analytics.filters.label')} className="flex rounded-xl border border-border/70 bg-muted/[0.18] p-3">
        <Select value={platformValue} onValueChange={(value) => onPlatformsChange(value === 'all' ? [] : [value])}>
          <SelectTrigger aria-label={t('analytics.filters.platforms')} className="h-9 w-[142px] bg-background">
            <SlidersHorizontal className="mr-1 h-3.5 w-3.5" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('analytics.filters.allPlatforms')}</SelectItem>
            {platformChoices.map((platform) => (
              <SelectItem key={platform} value={platform}>{t(`analytics.platforms.${platform}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </section>
    );
  }

  return (
    <section aria-label={t('analytics.filters.label')} className="flex flex-col gap-3 rounded-xl border border-border/70 bg-muted/[0.18] p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={range} onValueChange={(value) => onRangeChange(value as AnalyticsRange)}>
          <SelectTrigger aria-label={t('analytics.filters.dateRange')} className="h-9 w-[132px] bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ANALYTICS_RANGES.map((value) => (
              <SelectItem key={value} value={value}>{t(`analytics.ranges.${value}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <MultiSelectPopover
          label={t('analytics.filters.platforms')}
          allLabel={t('analytics.filters.allPlatforms')}
          countLabel={t('analytics.filters.platformsCount', { count: platforms.length })}
          options={platformChoices.map((platform) => ({ value: platform, label: t(`analytics.platforms.${platform}`) }))}
          value={platforms}
          onChange={onPlatformsChange}
          idPrefix="analytics-platform"
          icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
        />
      </div>
      <Button type="button" variant="outline" size="sm" className="h-9 shrink-0 self-start sm:self-auto" onClick={onRefresh} disabled={refreshing || refreshDisabled}>
        <RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
        {refreshLabel || t('analytics.refresh.label')}
      </Button>
    </section>
  );
};
